import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { enqueueSnackbar } from 'notistack';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useDossierDocumentsCtx } from '@europrocurement/l2d-modules/providers';
import { DOC_TYPE_DOWNLOADABLE, DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import { useDocumentSign } from '../../../hooks';
import { useDocumentSignModalContext, useDossierDocumentSignContext } from '../../../providers';
import { ModalPaper } from '../ExportDossierModal/ModalPaper';
import DocumentsCollection from './DocumentsCollection/DocumentsCollection';
import RecipientForm from './RecipientForm/RecipientForm';
import { DisplayedDocument, SignableDocumentRecipient } from '../../../providers/types';
import RecipientsCollection from './RecipientsCollection/RecipientsCollection';
import DocumentForm from './DocumentForm/DocumentForm';

export type DocumentSignFormData = {
    recipients: Array<unknown>;
};

type SubjectFormData = {
    subject: string;
};

export type DocumentSignModalProps = {
    subject: string;
};

const DocumentSignModal: React.FunctionComponent<DocumentSignModalProps> = function ({ subject }) {
    const { palette } = useTheme();
    const {
        dossierId,
        prescripteur,
        documents,
        recipients,
        modalOpen,
        allowAddDocument,
        addRecipient,
        removeRecipient,
        setModalOpen,
        setAllowAddDocument,
        replaceDocuments,
    } = useDocumentSignModalContext();
    const { loadDossiers } = useDossierDocumentSignContext();
    const { getDossierDocumentsByCategory } = useDossierDocumentsCtx();
    const { submitDossier } = useDocumentSign();
    const [currentAction, setCurrentAction] = useState<
        'READ' | 'WRITE_RECIPIENT' | 'WRITE_DOCUMENT'
    >(allowAddDocument ? 'WRITE_DOCUMENT' : 'WRITE_RECIPIENT');
    const [processing, setProcessing] = useState<boolean>(false);
    const availableDocuments = [
        getDossierDocumentsByCategory({ categoryId: DOC_TYPE_REQUIRED }),
        getDossierDocumentsByCategory({ categoryId: DOC_TYPE_REQUIRED, additionalDocs: true }),
        getDossierDocumentsByCategory({ categoryId: DOC_TYPE_DOWNLOADABLE }),
        getDossierDocumentsByCategory({ categoryId: DOC_TYPE_DOWNLOADABLE, additionalDocs: false }),
    ]
        .flat()
        .filter((document) => document.fichierId !== undefined);

    const handleShowRecipientForm = () => {
        setCurrentAction('WRITE_RECIPIENT');
    };

    const handleShowDocumentForm = () => {
        setCurrentAction('WRITE_DOCUMENT');
    };

    const handleCancelForm = () => {
        setCurrentAction('READ');
    };

    const handleSubmitRecipient = (recipient: SignableDocumentRecipient) => {
        addRecipient(recipient);
        setCurrentAction('READ');
    };

    const handleRemoveRecipient = (recipient: SignableDocumentRecipient) => {
        removeRecipient(recipient);

        if (recipients.length - 1 === 0) {
            setCurrentAction('WRITE_RECIPIENT');
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleSubmit: SubmitHandler<SubjectFormData> = async (formData: SubjectFormData) => {
        if (!dossierId) {
            throw new Error('Dossier id is required');
        }

        if (!prescripteur) {
            throw new Error('Prescripteur is required');
        }

        if (!documents.length) {
            throw new Error('At least one document is required');
        }

        if (!recipients.length) {
            throw new Error('At least one recipient is required');
        }

        setProcessing(true);

        try {
            await submitDossier(dossierId, formData.subject, prescripteur, documents, recipients);
            loadDossiers();
            setModalOpen(false);
            setAllowAddDocument(false);
            enqueueSnackbar('Le dossier de signature a bien été envoyé');
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Une erreur est survenue lors de l'envoi du document à signer", {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleSubmitDocument = (newDocuments: Array<DisplayedDocument>) => {
        const signableDocs = newDocuments
            .filter((document) => document.fichierId !== undefined)
            .map((document) => ({
                documentName: document.label,
                documentFileName: document.fileName,
                fichierId: document.fichierId as number,
            }));
        replaceDocuments(signableDocs);

        if (recipients.length > 0) {
            setCurrentAction('READ');
        } else {
            setCurrentAction('WRITE_RECIPIENT');
        }
    };

    const getModalTitle = () => {
        if (currentAction === 'WRITE_DOCUMENT') {
            return 'Sélectionner les documents à envoyer';
        }

        if (currentAction === 'WRITE_RECIPIENT') {
            return 'Ajouter un destinataire';
        }

        return documents.length > 1
            ? 'Envoyer les documents à signer'
            : 'Envoyer le document à signer';
    };

    const formStructure: FormStructure[] = [
        {
            type: 'text',
            name: 'subject',
            label: 'Sujet',
            rules: {
                required: 'Ce champ est requis',
            },
        },
    ];

    const formContext = useForm<FieldValues>({
        mode: 'onChange',
        defaultValues: {
            subject,
        },
    });

    const formProps = {
        formContext,
        onSubmit: handleSubmit,
        submitButton: {
            displayed: false,
        },
    };

    useEffect(() => {
        if (modalOpen) {
            setCurrentAction(allowAddDocument ? 'WRITE_DOCUMENT' : 'WRITE_RECIPIENT');
            setProcessing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen]);

    return (
        <Dialog
            open={modalOpen}
            PaperComponent={ModalPaper}
        >
            <IconButton
                aria-label="close"
                onClick={handleCloseModal}
                sx={{
                    fontSize: '16px',
                    position: 'absolute',
                    right: '20px',
                    top: '30px',
                    color: palette.grey[500],
                }}
            >
                <FaOptionIcon icon={['fas', 'close']} />
            </IconButton>
            <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '1em' }}>
                <Typography variant="l2dh6">{getModalTitle()}</Typography>
                {currentAction === 'READ' ? (
                    <>
                        <FlexyForm
                            formStructure={formStructure}
                            {...formProps}
                        />
                        <DocumentsCollection documents={documents} />
                        {allowAddDocument ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    columnGap: '1em',
                                }}
                            >
                                <Button
                                    variant="text"
                                    onClick={handleShowDocumentForm}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        columnGap: '0.5em',
                                        color: 'text.gris50',
                                    }}
                                    disabled={processing}
                                >
                                    <FaOptionIcon icon={['fas', 'plus']} />
                                    Modifier les documents à envoyer
                                </Button>
                            </Box>
                        ) : null}
                        <RecipientsCollection
                            recipients={recipients}
                            onRemoveRecipient={processing ? undefined : handleRemoveRecipient}
                        />
                        <Box
                            sx={{ display: 'flex', justifyContent: 'flex-start', columnGap: '1em' }}
                        >
                            <Button
                                variant="text"
                                onClick={handleShowRecipientForm}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    columnGap: '0.5em',
                                    color: 'text.gris50',
                                }}
                                disabled={processing}
                            >
                                <FaOptionIcon icon={['fas', 'plus']} />
                                Ajouter un destinataire
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', columnGap: '1em' }}>
                            <Button
                                variant="text"
                                onClick={handleCloseModal}
                                sx={{ color: palette.primary.dark }}
                                disabled={processing}
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="contained"
                                onClick={formContext.handleSubmit(handleSubmit)}
                                sx={{
                                    display: 'flex',
                                    columnGap: '0.5em',
                                    padding: '16px 24px',
                                    background: palette.primary.dark,
                                }}
                                disabled={processing}
                            >
                                {processing && (
                                    <CircularProgress
                                        color="inherit"
                                        size="16px"
                                    />
                                )}
                                {!processing && (
                                    <FaOptionIcon
                                        icon={['fas', 'paper-plane']}
                                        sx={{ fontSize: '12px', lineHeight: 0 }}
                                    />
                                )}
                                {documents.length > 1
                                    ? 'Envoyer les documents'
                                    : 'Envoyer le document'}
                            </Button>
                        </Box>
                    </>
                ) : null}
                {allowAddDocument && currentAction === 'WRITE_DOCUMENT' ? (
                    <DocumentForm
                        selectedDocuments={availableDocuments.filter((availableDoc) =>
                            documents.find(
                                (signableDoc) => signableDoc.fichierId === availableDoc.fichierId,
                            ),
                        )}
                        documents={availableDocuments}
                        onSubmit={handleSubmitDocument}
                        onCancel={documents.length > 0 ? handleCancelForm : handleCloseModal}
                    />
                ) : null}
                {currentAction === 'WRITE_RECIPIENT' && (
                    <RecipientForm
                        onAddRecipient={handleSubmitRecipient}
                        onCancel={recipients.length > 0 ? handleCancelForm : handleCloseModal}
                    />
                )}
            </Box>
        </Dialog>
    );
};

export default DocumentSignModal;
